import { type FormEvent, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from '@beamx/shared';
import { email } from '../lib/api/email.js';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-filled From address (e.g. noreply@verified-domain). Editable. */
  defaultFrom?: string;
  /** Inline content to send as the test. */
  subject: string;
  html: string;
  text: string;
}

/**
 * Sends a one-off inline test message via POST /v1/email/send. Reused by the sender-domain
 * cards (canned content) and the template editor (the last rendered draft). Always sends
 * inline content (Mode A) so it tests exactly what's on screen, independent of publish state.
 */
export function SendTestDialog({ open, onOpenChange, defaultFrom = '', subject, html, text }: Props) {
  const [to, setTo] = useState('');
  const [from, setFrom] = useState(defaultFrom);

  useEffect(() => {
    if (open) setFrom(defaultFrom);
  }, [open, defaultFrom]);

  const send = useMutation({
    mutationFn: () =>
      email.send({
        to: to.trim(),
        from: { email: from.trim() },
        subject,
        html,
        text,
        categories: ['test'],
      }),
    onSuccess: (res) => {
      toast.success('Test sent', { description: `Message ${res.messageId}` });
      onOpenChange(false);
      setTo('');
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Send failed'),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    send.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send a test</DialogTitle>
          <DialogDescription>
            Delivers this exact content as a one-off. The From domain must be a verified
            sender domain for this tenant.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="test-from">From</Label>
            <Input id="test-from" type="email" required value={from} onChange={(e) => setFrom(e.target.value)} placeholder="noreply@acme.example" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="test-to">Send to</Label>
            <Input id="test-to" type="email" required autoFocus value={to} onChange={(e) => setTo(e.target.value)} placeholder="you@example.com" />
          </div>
          <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            Subject: <span className="font-medium text-foreground">{subject || '—'}</span>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={send.isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={send.isPending}>
              <Send className="h-4 w-4" />
              Send test
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
